import {
  Building2,
  User,
  LayoutDashboard,
  CircleUserRound,
  Settings,
  ChartSpline,
  Cuboid,
  ChevronDown,
  Handshake,
  Users,
  AlertTriangle,
  Shield,
  Mail,
} from "lucide-react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { LogoWithText } from "../../Logo";

const Sidebar = ({ sidebarExpanded }) => {
  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  });
  const [usageOpen, setUsageOpen] = useState(false);
  const location = window.location.pathname;
  const navigate = useNavigate();
  
  // Helper function to check if user has access to a route (defined inside component to use current user state)
  const hasRouteAccess = useMemo(() => {
    return (routePath) => {
      if (!user) {
        console.log(`[hasRouteAccess] ${routePath}: No user - DENIED`);
        return false;
      }
      
      // Super Admin has access to all routes
      if (user.role === "super admin" || user.role === "super_admin") {
        console.log(`[hasRouteAccess] ${routePath}: Super admin - ALLOWED`);
        return true;
      }
      
      // If user is admin, check adminRole
      if (user.role === "admin") {
        // If adminRole doesn't exist, is null, or is undefined, give full access (backward compatibility)
        if (!user.adminRole || user.adminRole === null || user.adminRole === undefined) {
          console.log(`[hasRouteAccess] ${routePath}: Admin with no role - ALLOWED (backward compat)`);
          return true;
        }
        
        // If adminRole exists, it must be an object (not a string ID)
        if (typeof user.adminRole !== 'object' || user.adminRole === null) {
          console.log(`[hasRouteAccess] ${routePath}: AdminRole is not an object (${typeof user.adminRole}) - DENIED`);
          return false;
        }
        
        // AdminRole is an object - check allowedRoutes
        // If allowedRoutes doesn't exist or is not an array, deny access
        if (!user.adminRole.allowedRoutes || !Array.isArray(user.adminRole.allowedRoutes)) {
          console.log(`[hasRouteAccess] ${routePath}: allowedRoutes missing or not array - DENIED`, {
            hasAllowedRoutes: !!user.adminRole.allowedRoutes,
            type: typeof user.adminRole.allowedRoutes,
            isArray: Array.isArray(user.adminRole.allowedRoutes)
          });
          return false;
        }
        
        // If allowedRoutes is empty array, deny all access
        if (user.adminRole.allowedRoutes.length === 0) {
          console.log(`[hasRouteAccess] ${routePath}: allowedRoutes is empty - DENIED`);
          return false;
        }
        
        // Check exact match or parent route match
        console.log(`[hasRouteAccess] ${routePath}: Checking against allowedRoutes:`, JSON.stringify(user.adminRole.allowedRoutes));
        const hasAccess = user.adminRole.allowedRoutes.some(allowedRoute => {
          // Exact match
          if (allowedRoute === routePath) {
            console.log(`[hasRouteAccess] ${routePath}: ✓ Exact match with "${allowedRoute}"`);
            return true;
          }
          
          // Parent route match (e.g., /admin/usage allows /admin/usage/individuals)
          // BUT: Special case - if allowedRoute is "/admin", it should ONLY match "/admin" exactly
          // This prevents "/admin" from granting access to all child routes
          if (allowedRoute === "/admin") {
            // "/admin" only matches "/admin" exactly (already checked above)
            console.log(`[hasRouteAccess] ${routePath}: ✗ "/admin" only matches exactly, not "${routePath}"`);
            return false;
          }
          
          // For other routes, allow parent route matching
          if (routePath.startsWith(allowedRoute + "/")) {
            console.log(`[hasRouteAccess] ${routePath}: ✓ Parent match with "${allowedRoute}"`);
            return true;
          }
          
          console.log(`[hasRouteAccess] ${routePath}: ✗ No match with "${allowedRoute}"`);
          return false;
        });
        
        if (!hasAccess) {
          console.log(`[hasRouteAccess] ${routePath}: NOT in allowedRoutes - DENIED`);
        }
        
        return hasAccess;
      }
      
      // Default: no access for non-admin roles
      console.log(`[hasRouteAccess] ${routePath}: Not admin role (${user.role}) - DENIED`);
      return false;
    };
  }, [user]);
  
  // Update user when localStorage changes or location changes
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const userData = localStorage.getItem("user");
        const parsedUser = userData ? JSON.parse(userData) : null;
        setUser(parsedUser);
        
        // Debug: Log user adminRole info
        if (parsedUser?.adminRole) {
          console.log('[Sidebar] User adminRole loaded:', {
            roleName: parsedUser.adminRole.name,
            allowedRoutes: parsedUser.adminRole.allowedRoutes,
            routeCount: parsedUser.adminRole.allowedRoutes?.length || 0,
            isArray: Array.isArray(parsedUser.adminRole.allowedRoutes),
            adminRoleType: typeof parsedUser.adminRole
          });
        } else if (parsedUser?.role === "admin") {
          console.log('[Sidebar] Admin user with no adminRole - will have full access');
        }
      } catch {
        setUser(null);
      }
    };
    
    // Check on mount and when location changes
    handleStorageChange();
    
    // Listen for storage events (when localStorage is updated from another tab/window)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check when location changes (user might have logged in)
    const interval = setInterval(handleStorageChange, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [location]);
  
  // Debug: Log route access checks
  useEffect(() => {
    if (user?.adminRole) {
      console.log('[Sidebar] Route access checks:', {
        userRole: user.role,
        adminRoleName: user.adminRole.name,
        allowedRoutes: user.adminRole.allowedRoutes,
        allowedRoutesString: JSON.stringify(user.adminRole.allowedRoutes),
        dashboard: hasRouteAccess("/admin"),
        organizations: hasRouteAccess("/admin/organizations"),
        requests: hasRouteAccess("/admin/requests"),
        personas: hasRouteAccess("/admin/personas"),
        plans: hasRouteAccess("/admin/plans"),
        models: hasRouteAccess("/admin/models"),
        partners: hasRouteAccess("/admin/partners"),
        sentry: hasRouteAccess("/admin/sentry-dashboard"),
      });
    }
  }, [user, hasRouteAccess]);
  // Determine if usage sub-routes are active
  const usageActive = location.startsWith("/admin/usage");

  // Auto-collapse usage submenu if not on usage or its subroutes
  useEffect(() => {
    if (!location.startsWith("/admin/usage")) {
      setUsageOpen(false);
    }
  }, [location]);

  // Handler for Usage row
  const handleUsageClick = (e) => {
    e.preventDefault();
    if (!usageOpen) {
      if (!location.startsWith("/admin/usage")) {
        navigate("/admin/usage");
      }
      setUsageOpen(true);
    } else {
      setUsageOpen(false);
    }
  };

  return (
    <div
      className={`${
        sidebarExpanded ? "w-64" : "w-0"
      } border-r border-bd-50 bg-bg-60 flex flex-col transition-all duration-300 ease-in-out overflow-hidden`}
    >
      {/* Header with Logo and Search */}
      <div className="flex items-center justify-between pt-[14px] px-3 pb-2">
        <div className="flex items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <LogoWithText
                className="h-7 w-auto max-w-32"
                fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
              />
              {user?.role && (
                <span className="ml-2 px-1.5 py-0.5 rounded-md text-xs font-mono font-semibold bg-bg-40 text-fg-60">
                  {user.role.replace(/_/g, " ").toUpperCase()}
                </span>
              )}
              {user?.adminRole && (
                <span className="ml-2 px-1.5 py-0.5 rounded-md text-xs font-mono font-semibold bg-ac-01/20 text-ac-01">
                  {user.adminRole.name || "Role"}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>
      
      {/* Debug Info - Remove in production */}
      {user?.adminRole && (
        <div className="px-3 pb-2 text-xs text-fg-60">
          <div>Role: {user.adminRole.name || "N/A"}</div>
          <div>Routes: {user.adminRole.allowedRoutes?.length || 0}</div>
          {user.adminRole.allowedRoutes && user.adminRole.allowedRoutes.length > 0 && (
            <div className="mt-1 text-fg-60/70">
              {user.adminRole.allowedRoutes.slice(0, 3).join(", ")}
              {user.adminRole.allowedRoutes.length > 3 && "..."}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex-grow overflow-y-auto custom-scrollbar">
        <div className="px-3 py-4">
          <div className="space-y-1">
            {hasRouteAccess("/admin") && (
              <NavLink
                to="/admin"
                end
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <LayoutDashboard size={18} className="mr-3 flex-shrink-0" />
                <span>Dashboard</span>
              </NavLink>
            )}

            {hasRouteAccess("/admin/organizations") && (
              <NavLink
                to="/admin/organizations"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Building2 size={18} className="mr-3 flex-shrink-0" />
                <span>Organizations</span>
              </NavLink>
            )}
            {hasRouteAccess("/admin/requests") && (
              <NavLink
                to="/admin/requests"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <CircleUserRound size={18} className="mr-3 flex-shrink-0" />
                <span>Individuals</span>
              </NavLink>
            )}
            {hasRouteAccess("/admin/personas") && (
              <NavLink
                to="/admin/personas"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Users size={18} className="mr-3 flex-shrink-0" />
                <span>Personas</span>
              </NavLink>
            )}
            {/* Newsletter & Email */}
            {hasRouteAccess("/admin/newsletter") && (
              <NavLink
                to="/admin/newsletter"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Mail size={18} className="mr-3 flex-shrink-0" />
                <span>Newsletter & Email</span>
              </NavLink>
            )}
            {/* Usage with submenu */}
            {hasRouteAccess("/admin/usage") && (
              <div className="flex flex-col">
                <div
                  className={`flex items-center w-full px-2 py-2 text-sm rounded-md focus:outline-none transition-colors ${
                    usageActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`}
                >
                  <NavLink
                    to="/admin/usage"
                    end
                    className="flex items-center flex-1 min-w-0"
                    style={{ minWidth: 0 }}
                    onClick={() => {
                      if (!usageOpen || !location.startsWith("/admin/usage")) {
                        setUsageOpen(true);
                      }
                    }}
                  >
                    <ChartSpline size={18} className="mr-3 flex-shrink-0" />
                    <span className="truncate">Usage</span>
                  </NavLink>
                  <button
                    type="button"
                    aria-label="Expand submenu"
                    className="ml-2 p-1 rounded focus:outline-none hover:bg-bg-50 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUsageOpen((prev) => !prev);
                    }}
                  >
                    <ChevronDown
                      size={12}
                      className={`transition-transform ${
                        usageOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
                <div
                  className={`ml-8 usage-submenu overflow-hidden transition-all duration-300 ease-in-out ${
                    usageOpen
                      ? "max-h-32 opacity-100"
                      : "max-h-0 opacity-0 pointer-events-none"
                  }`}
                  style={{
                    transitionProperty: "max-height, opacity",
                  }}
                >
                  <div className="space-y-1 my-1">
                    {hasRouteAccess("/admin/usage/individuals") && (
                      <NavLink
                        to="/admin/usage/individuals"
                        className={({ isActive }) =>
                          `flex items-center w-full px-2 py-2 text-sm rounded-md usage-subitem ${
                            isActive
                              ? "bg-bg-30 text-fg-50"
                              : "text-fg-60 hover:bg-bg-50/30"
                          }`
                        }
                      >
                        <span>Individuals</span>
                      </NavLink>
                    )}
                    {hasRouteAccess("/admin/usage/organizations") && (
                      <NavLink
                        to="/admin/usage/organizations"
                        className={({ isActive }) =>
                          `flex items-center w-full px-2 py-2 text-sm rounded-md usage-subitem ${
                            isActive
                              ? "bg-bg-30 text-fg-50"
                              : "text-fg-60 hover:bg-bg-50/30"
                          }`
                        }
                      >
                        <span>Organizations</span>
                      </NavLink>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Plans */}
            {hasRouteAccess("/admin/plans") && (
              <NavLink
                to="/admin/plans"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Settings size={18} className="mr-3 flex-shrink-0" />
                <span>Plan Settings</span>
              </NavLink>
            )}
            {/* Models */}
            {hasRouteAccess("/admin/models") && (
              <NavLink
                to="/admin/models"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Cuboid size={18} className="mr-3 flex-shrink-0" />
                <span>Models</span>
              </NavLink>
            )}
            {/* Partners */}
            {hasRouteAccess("/admin/partners") && (
              <NavLink
                to="/admin/partners"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Handshake size={18} className="mr-3 flex-shrink-0" />
                <span>Partners</span>
              </NavLink>
            )}
            {/* Sentry Dashboard */}
            {hasRouteAccess("/admin/sentry-dashboard") && (
              <NavLink
                to="/admin/sentry-dashboard"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <AlertTriangle size={18} className="mr-3 flex-shrink-0" />
                <span>App Monitoring</span>
              </NavLink>
            )}
            {/* Role Management - Only for Super Admin */}
            {(user?.role === "super admin" || user?.role === "super_admin") && (
              <NavLink
                to="/admin/roles"
                className={({ isActive }) =>
                  `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                    isActive
                      ? "bg-bg-40 text-fg-50"
                      : "text-fg-60 hover:bg-bg-50/30"
                  }`
                }
              >
                <Shield size={18} className="mr-3 flex-shrink-0" />
                <span>Role & User Management</span>
              </NavLink>
            )}
          </div>
        </div>
      </div>

      {/* Admin Profile */}
      <div className="p-3">
        <div className="p-3 flex items-center hover:bg-bg-50 rounded-md cursor-pointer">
          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-ac-02 flex items-center justify-center text-fg-40 shadow-sm">
            <User size={16} />
          </div>
          {sidebarExpanded && (
            <div className="ml-3">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs text-fg-60">{user.email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
